import { createSlice } from '@reduxjs/toolkit';
import { THIRTY, campaignStatusTypes } from 'appConstants';
import {
  fetchCampaigns,
  fetchCampaignOptions,
  fetchScheduledMessages,
  fetchCampaignCandidates,
  addCandidateToCampaign,
  getCampaignAriSequences,
  addCampaign,
  removeMultiJobCandidates,
  setCandidateAddedToCampaign,
  setCampaignUpdated,
  setCampaignAdded,
  fetchCampaign,
  updateCampaign,
  resetCampaign,
  fetchCandidateJobMax,
  resetDeleteStatusCandidateCampaign,
  enableDlcForm,
  resetCampaignJobData,
  setCampaignJobData,
  fetchCampaignBGEvents,
  resetCampaignOptions,
  addBulkCandidateToCampaign,
} from '../actions';
import { dedupeOptions } from 'util/helpers';
import { currentTime } from 'components/Campaigns/helper';
import { SIXTY, THIRTY_TO_SIXTY_DAYS, count } from 'components/Campaigns/constants';
import { commonStateUpdates } from './commonStateUpdate';

const initialJobObj = {
  name: '',
  code: '',
  url: '',
  status: campaignStatusTypes.OPEN,
  outboundAri: {},
  type: '',
  isEnabled: true,
  sequenceDefinitionId: '',
};

const initialCampaignJobObj = {
  campaignName: '',
  isReengagement: false,
  jobCode: '',
  url: '',
  automaticResponse: '',
  isAri: false,
  sequence: '',
  reEngagementUrl: '',
  fromDaysAgo: THIRTY,
  toDaysAgo: SIXTY,
  lastContacted: {
    name: THIRTY_TO_SIXTY_DAYS,
    value: THIRTY_TO_SIXTY_DAYS,
  },
  messageTemplate: '',
  candidatesNumberWithTags: count.ZERO,
  selectedTags: [],
  time: currentTime(),
  reEngagementText: '',
  sendAs: '',
};

const initialState = {
  showDlcForm: false,
  isLoading: false,
  page: count.ONE,
  deleteCandidateId: false,
  campaignsCount: 0,
  campaigns: [],
  candidateAddedToCampaign: false,
  scheduledMessages: {
    messages: [],
    isLoading: false,
    count: count.ZERO,
    page: count.ONE,
  },
  campaignCandidates: {
    candidates: [],
    isLoading: false,
    count: count.ZERO,
    page: count.ONE,
  },
  campaignCandidatesCount: {
    isCountLoading: false,
    candidatesCount: count.ZERO,
  },
  ariState: {
    thread: {
      sequenceEnabled: false,
    },
    isEnabling: false,
    isDisabling: false,
    isLoading: false,
    sequences: [],
    unusedSequenceTypes: [],
  },
  campaignAddition: false,
  campaignJobData: initialCampaignJobObj,
  job: initialJobObj,
  jobMax: {
    firstName: '',
    jobTitle: '',
    lastName: '',
  },
  jobMaxLoading: false,
  isCampaignLoading: false,
  isCampaignFetched: false,
  isCampaignUpdated: false,
  campaignAdding: false,
  isCampaignUpdating: false,
  campaignOptions: [],
  isCampaignOptionsLoading: false,
  campaignOptionsCount: count.ZERO,
  processingEventsCount: count.ONE,
  isImportInProgress: false,
  itemsProcessed: count.ZERO,
  totalItems: count.ZERO,
};

const campaignsSlice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(setCandidateAddedToCampaign, (state) => {
      state.candidateAddedToCampaign = false;
    });

    builder.addCase(enableDlcForm, (state, action) => {
      state.showDlcForm = action.payload;
    });

    builder.addCase(fetchCampaigns.pending, (state) => {
      state.isLoading = true;
      state.campaignAddition = false;
    });

    builder.addCase(fetchCampaigns.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.campaigns = action.payload.jobs || [];
      state.isLoading = false;
      state.page = page;
      state.campaignsCount = items;
      state.campaignAddition = false;
      state.candidateDeletion = false;
    });

    builder.addCase(fetchCampaigns.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(fetchCampaignOptions.pending, (state) => {
      state.isCampaignOptionsLoading = true;
    });

    builder.addCase(fetchCampaignOptions.fulfilled, (state, action) => {
      const { paging = {} } = action.payload;
      const campaigns = action.payload.jobs || [];
      state.campaignOptions = dedupeOptions(state.campaignOptions, campaigns);
      state.isCampaignOptionsLoading = false;
      state.campaignOptionsCount = paging.items || state.campaignOptions.length;
    });

    builder.addCase(fetchCampaignOptions.rejected, (state, action) => {
      state.isCampaignOptionsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(resetCampaignOptions, (state) => {
      state.campaignOptions = [];
    });

    builder.addCase(fetchScheduledMessages.pending, (state) => {
      state.scheduledMessages.isLoading = true;
    });

    builder.addCase(fetchScheduledMessages.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.scheduledMessages.messages = action.payload.messages || [];
      state.scheduledMessages.isLoading = false;
      state.scheduledMessages.page = page;
      state.scheduledMessages.count = items;
      state.campaignAddition = false;
    });

    builder.addCase(fetchScheduledMessages.rejected, (state, action) => {
      state.scheduledMessages.isLoading = false;
      state.scheduledMessages.error = action.payload.error;
    });

    builder.addCase(fetchCampaignCandidates.pending, (state) => {
      state.campaignCandidates.isLoading = true;
    });

    builder.addCase(fetchCampaignCandidates.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.campaignCandidates.candidates = action.payload.candidates || [];
      state.campaignCandidates.isLoading = false;
      state.campaignCandidates.page = page;
      state.campaignCandidates.count = items;
      state.campaignAddition = false;
    });

    builder.addCase(fetchCampaignCandidates.rejected, (state, action) => {
      state.campaignCandidates.isLoading = false;
      state.campaignCandidates.error = action.payload.error;
    });

    builder.addCase(addCandidateToCampaign.fulfilled, (state) => {
      state.candidateAddedToCampaign = true;
    });

    builder.addCase(getCampaignAriSequences.pending, (state) => {
      state.ariState.isLoading = true;
    });

    builder.addCase(getCampaignAriSequences.fulfilled, (state, action) => {
      const { sequences = [], unusedSequenceTypes = [] } = action.payload;

      state.ariState.isLoading = false;
      state.ariState.sequences = sequences;
      state.ariState.unusedSequenceTypes = unusedSequenceTypes;
    });

    builder.addCase(getCampaignAriSequences.rejected, (state, action) => {
      state.ariState.isLoading = false;
      state.ariState.error = action.payload.error;
    });

    builder.addCase(addCampaign.pending, (state) => {
      state.campaignAddition = false;
      state.campaignAdding = true;
      state.isLoading = true;
    });

    builder.addCase(addCampaign.fulfilled, (state, action) => {
      state.campaignAddition = true;
      state.job = action.payload.job;
      state.campaignAdding = false;
      state.isLoading = false;
      /* Adding new campaign the existing campaign list to avoid extra call to fetch-all-campaign api*/
      state.campaigns.push(state.job);
    });

    builder.addCase(addCampaign.rejected, (state, action) => {
      state.campaignAddition = false;
      state.error = action.payload.error;
      state.campaignAdding = false;
      state.isLoading = false;
    });

    builder.addCase(resetDeleteStatusCandidateCampaign, (state) => {
      state.deleteCandidateId = false;
    });

    builder.addCase(removeMultiJobCandidates.pending, (state) => {
      state.campaignCandidateUpdating = true;
      state.deleteCandidateId = false;
    });

    builder.addCase(removeMultiJobCandidates.fulfilled, (state, action) => {
      state.campaignCandidateUpdating = false;
      state.job = action.payload.job;
      state.deleteCandidateId = true;
    });

    builder.addCase(removeMultiJobCandidates.rejected, (state, action) => {
      state.campaignCandidateUpdating = false;
      state.error = action.payload.error;
      state.deleteCandidateId = false;
    });

    builder.addCase(fetchCampaign.pending, (state) => {
      state.isCampaignLoading = true;
      state.isCampaignFetched = false;
      state.job = {};
      state.error = false;
    });

    builder.addCase(fetchCampaign.fulfilled, (state, action) => {
      state.isCampaignLoading = false;
      state.isCampaignFetched = true;
      state.job = action.payload.job;
      state.error = false;
    });

    builder.addCase(fetchCampaign.rejected, (state, action) => {
      state.isCampaignLoading = false;
      state.isCampaignFetched = false;
      state.error = action.payload.message;
    });

    builder.addCase(updateCampaign.pending, (state) => {
      state.job = {};
      state.isCampaignUpdated = false;
      state.isCampaignUpdating = true;
      state.isCampaignLoading = true;
    });

    builder.addCase(updateCampaign.fulfilled, (state, action) => {
      state.isCampaignUpdated = true;
      state.job = action.payload.job;
      state.isCampaignUpdating = false;
      state.isCampaignLoading = false;
      /** Updating the existing campaign with updated url from update api response.*/
      state.campaigns.forEach((campaign) => {
        const { url } = state.job;
        if (campaign.id === state.job.id) {
          campaign.url = url;
        }
      });
    });

    builder.addCase(updateCampaign.rejected, (state, action) => {
      state.isCampaignUpdated = false;
      state.error = action.payload.error;
      state.isCampaignUpdating = false;
      state.isCampaignLoading = false;
    });

    builder.addCase(setCampaignUpdated, (state, action) => {
      state.isCampaignUpdated = action.payload;
    });

    builder.addCase(setCampaignAdded, (state, action) => {
      state.campaignAddition = action.payload;
    });

    builder.addCase(fetchCandidateJobMax.pending, (state) => {
      state.jobMaxLoading = true;
    });

    builder.addCase(fetchCandidateJobMax.fulfilled, (state, action) => {
      state.jobMaxLoading = false;
      state.jobMax = action.payload.max;
    });

    builder.addCase(fetchCandidateJobMax.rejected, (state, action) => {
      state.error = action.payload.error;
      state.jobMaxLoading = false;
    });

    builder.addCase(resetCampaign, (state) => {
      state.job = initialJobObj;
      state.isCampaignFetched = false;
    });

    builder.addCase(resetCampaignJobData, (state) => {
      state.campaignJobData = initialCampaignJobObj;
      state.job = initialJobObj;
    });

    builder.addCase(setCampaignJobData, (state, action) => {
      state.campaignJobData = action.payload.campaignJobData;
    });

    builder.addCase(fetchCampaignBGEvents.fulfilled, (state, action) => {
      commonStateUpdates(state, action);
    });

    builder.addCase(addBulkCandidateToCampaign.fulfilled, (state, action) => {
      state.isImportInProgress = Boolean(action.payload.backgroundEventId);
    });
  },
});

export default campaignsSlice;
